import { FULL_WIDTH, LEFT_RIGHT_MEDIA, THREE_COLUMNS, TWO_COLUMNS } from '@opengov/stories-shared/constants';
import { assertNotNil, assertUnreachable } from '../../../../../../../../types/typeUtils';
import { MEDIA_IMAGE, MEDIA_TABLE_TILE, MEDIA_TILE } from '../../../../../../../constants';

export type HtmlExtractionResult =
  | { mediaType: typeof MEDIA_TILE | typeof MEDIA_TABLE_TILE; embedId: string; editorDataUuid: string }
  | { mediaType: typeof MEDIA_IMAGE; editorDataUuid: string; imageSrc: string };

export type AiEnabledMediaType = typeof MEDIA_TILE | typeof MEDIA_TABLE_TILE | typeof MEDIA_IMAGE;

export function extractIdsForMediaType({
  mediaType,
  sectionElement,
  mediaIndexAttr,
  storyContentGroup
}: {
  mediaType: AiEnabledMediaType;
  sectionElement: HTMLElement;
  mediaIndexAttr?: string;
  storyContentGroup: string;
}): HtmlExtractionResult {
  const { embedId, editorDataUuid, imageSrc } = extractIds({
    sectionElement,
    mediaIndexAttr,
    mediaType,
    storyContentGroup
  });

  switch (mediaType) {
    case MEDIA_TILE:
    case MEDIA_TABLE_TILE: {
      return {
        mediaType,
        embedId: assertNotNil(embedId, 'embedId'),
        editorDataUuid: assertNotNil(editorDataUuid, 'editorDataUuid')
      };
    }

    case MEDIA_IMAGE: {
      return {
        mediaType,
        editorDataUuid: assertNotNil(editorDataUuid, 'editorDataUuid'),
        imageSrc: assertNotNil(imageSrc, 'imageSrc')
      };
    }

    default:
      assertUnreachable(mediaType, 'mediaType');
  }
}

type ExtractedIds = {
  embedId: string;
  editorDataUuid: string | null;
  imageSrc: string | null;
};

function extractIds({
  sectionElement,
  mediaType,
  mediaIndexAttr,
  storyContentGroup
}: {
  sectionElement: HTMLElement;
  mediaType: AiEnabledMediaType;
  mediaIndexAttr?: string;
  storyContentGroup: string;
}): ExtractedIds {
  try {
    if (storyContentGroup === LEFT_RIGHT_MEDIA) {
      return extractIdsFromLeftRightLayout(sectionElement, mediaType);
    }

    if (storyContentGroup === FULL_WIDTH) {
      return extractFromFullWidthLayout(sectionElement, mediaType);
    }

    if (storyContentGroup === TWO_COLUMNS) {
      return extractFromTwoColumnsLayout(sectionElement, mediaIndexAttr, mediaType);
    }

    if (storyContentGroup === THREE_COLUMNS) {
      return extractFromThreeColumnsLayout(sectionElement, mediaIndexAttr, mediaType);
    }

    throw new Error(`Unsupported storyContentGroup: ${storyContentGroup}`);
  } catch (error) {
    console.error(error);
    return { embedId: null, editorDataUuid: null, imageSrc: null };
  }
}

function extractIdsFromLeftRightLayout(sectionElement: HTMLElement, mediaType: AiEnabledMediaType): ExtractedIds {
  const embedId = shouldHaveEmbedId(mediaType)
    ? sectionElement.querySelector('.media-block.has-media [data-embed-id]')?.getAttribute('data-embed-id') ?? null
    : null;

  const imageSrc = shouldHaveImage(mediaType)
    ? sectionElement.querySelector('.media-block.has-media img')?.getAttribute('src') ?? null
    : null;

  let editorDataUuid: string | null = null;
  const editables = sectionElement.querySelectorAll('.left-right-content .editable[data-uuid]');
  editorDataUuid = editables[1]?.getAttribute('data-uuid');

  return { embedId, editorDataUuid, imageSrc };
}

function extractFromFullWidthLayout(sectionElement: HTMLElement, mediaType: AiEnabledMediaType): ExtractedIds {
  const embedId = shouldHaveEmbedId(mediaType)
    ? sectionElement.querySelector('.media-block.has-media [data-embed-id]')?.getAttribute('data-embed-id') ?? null
    : null;

  const imageSrc = shouldHaveImage(mediaType)
    ? sectionElement.querySelector('.media-block.has-media img')?.getAttribute('src') ?? null
    : null;

  const editorDataUuid =
    sectionElement.querySelector('.full-width-content .editable[data-uuid]')?.getAttribute('data-uuid') ?? null;

  return { embedId, editorDataUuid, imageSrc };
}

function extractFromTwoColumnsLayout(
  sectionElement: HTMLElement,
  mediaIndexAttr: string,
  mediaType: AiEnabledMediaType
): ExtractedIds {
  const embedId = shouldHaveEmbedId(mediaType)
    ? sectionElement
        .querySelector(`.media-block.has-media[data-media-index="${mediaIndexAttr}"] [data-embed-id]`)
        ?.getAttribute('data-embed-id') ?? null
    : null;

  const imageSrc = shouldHaveImage(mediaType)
    ? sectionElement
        .querySelector(`.media-block.has-media[data-media-index="${mediaIndexAttr}"] img`)
        ?.getAttribute('src') ?? null
    : null;

  const editorDataUuid = getEditorDataUuid(sectionElement, '.col-2', mediaIndexAttr);

  return { embedId, editorDataUuid, imageSrc };
}

function extractFromThreeColumnsLayout(
  sectionElement: HTMLElement,
  mediaIndexAttr: string,
  mediaType: AiEnabledMediaType
): ExtractedIds {
  const embedId = shouldHaveEmbedId(mediaType)
    ? sectionElement
        .querySelector(`.media-block.has-media[data-media-index="${mediaIndexAttr}"] [data-embed-id]`)
        ?.getAttribute('data-embed-id') ?? null
    : null;

  const imageSrc = shouldHaveImage(mediaType)
    ? sectionElement
        .querySelector(`.media-block.has-media[data-media-index="${mediaIndexAttr}"] img`)
        ?.getAttribute('src') ?? null
    : null;

  const editorDataUuid = getEditorDataUuid(sectionElement, '.col-3', mediaIndexAttr);

  return { embedId, editorDataUuid, imageSrc };
}

export function hasNonHeaderEditable(sectionElement: HTMLElement): boolean {
  if (!sectionElement) return false;

  const classList = sectionElement.classList;

  if (classList.contains('full-width')) {
    const lastChild = sectionElement.lastElementChild;
    return !!lastChild && lastChild.classList.contains('full-width-content');
  }

  const hasClassWithEditableAndMediaBlock = (className: string, selector: string, mediaBlock: string): boolean => {
    return classList.contains(className) && !!sectionElement.querySelector(selector) && !!sectionElement.querySelector(mediaBlock);
  };

  return (
    classList.contains('left-right') ||
    hasClassWithEditableAndMediaBlock('two-columns', '.col-2 .editable', '.col-2 .media-block') ||
    hasClassWithEditableAndMediaBlock('three-columns', '.col-3 .editable', '.col-3 .media-block')
  );
}

function shouldHaveEmbedId(mediaType: AiEnabledMediaType): mediaType is typeof MEDIA_TILE | typeof MEDIA_TABLE_TILE {
  return mediaType === MEDIA_TILE || mediaType === MEDIA_TABLE_TILE;
}

function shouldHaveImage(mediaType: AiEnabledMediaType): mediaType is typeof MEDIA_IMAGE {
  return mediaType === MEDIA_IMAGE;
}

function getEditorDataUuid(sectionElement: HTMLElement, colClass: string, mediaIndexAttr: string): string | null {
  const cols = sectionElement.querySelectorAll(colClass);

  for (let i = 0; i < cols.length - 1; i++) {
    const mediaBlock = cols[i].querySelector(`.media-block.has-media[data-media-index="${mediaIndexAttr}"]`);
    if (mediaBlock != null && cols[i + 1].querySelector('.editable') != null) {
      return cols[i + 1].querySelector('.editable').getAttribute('data-uuid');
    }
  }
  return null;
}
